import { combineReducers, configureStore } from '@reduxjs/toolkit'
import type { UnknownAction, ThunkAction, Action, Middleware } from '@reduxjs/toolkit'

import { serviceDependencies } from 'src/services'
import type { ServiceDependenciesOptions } from 'src/services'
import { initSocketListenersMiddleware } from 'store/middlewares/initSocketListenersMiddleware'
import { paymentsMiddleware } from 'store/middlewares/paymentsMiddleware'
import { redeemMiddleware } from 'store/middlewares/redeemMiddleware'
import { verificationsMiddleware } from 'store/middlewares/verificationsMiddleware'

import { authMiddleware } from './middlewares/authMiddleware'
import rootReducers from './modules/rootReducer'

const defaultReduxMiddlewareOptions = {
  thunk: {
    extraArgument: serviceDependencies,
  },
  serializableCheck: false,
}

const middlewares: Middleware[] = [
  initSocketListenersMiddleware,
  verificationsMiddleware,
  redeemMiddleware,
  authMiddleware,
  paymentsMiddleware,
]

const setupStore = () => {
  const store = configureStore({
    reducer: combineReducers(rootReducers),
    middleware: (getDefaultMiddleware) =>
      /*
        With this configuration, we can use "serviceDependencies" in each thunk and access to our API via third argument

        Example:
        const getAccountInfo = () => (dispatch, getState, { gateway }) => {
          gateway.emit<GetAccountInfoRequest>({ type: 'GetAccountInfoRequest' })
        }

        Docs: https://redux-toolkit.js.org/api/getDefaultMiddleware
      */
      getDefaultMiddleware(defaultReduxMiddlewareOptions).concat(middlewares),
    devTools: process.env.PROFILE === 'dev',
  })

  return store
}

export default setupStore()

// Infer the type of makeStore
type AppStore = ReturnType<typeof setupStore>
// Infer the `ReduxState` and `AppDispatch` types from the store itself
type ReduxState = ReturnType<AppStore['getState']>

type AppDispatch = AppStore['dispatch']

type TypedThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  ReduxState,
  ServiceDependenciesOptions,
  UnknownAction | Action
>

export type { AppStore, AppDispatch, ReduxState, TypedThunk }
